import { autoinject, transient, computedFrom, observable, BindingEngine, useView } from "aurelia-framework";
import { DataType, SourceType, QueryFilter, DataGridColumn } from "../../../../../../models/queries/queries.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { I18N } from "aurelia-i18n";
import { BusinessUnitModel } from "../../../../../../models/customers/customer.models";
import { Guid } from "../../../../../../utilities/guid";
import { MobileHelper } from "../../../../../../helpers/mobile-helper";
import { CustomGridPageBase } from "../../../../common/custom-grid-page.base";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { RulesClientService } from "../../../../../../services/rules/rules-client.service";
import { DialogServiceConstants } from "../../../../../../constants/basics/dialog.constants";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { GenericConfirmationDialogModel } from "../../../../../../models/basics/generic-confirmation-dialog-model";
import { MenuItem } from "../../../../../components/menu/menu-item";
import { Reflection } from "../../../../../../utilities/reflection";

@autoinject
@transient()
@useView('views/pages/modules/customers/steps/rules/rule-collections.html')
export class BusinessUnitRuleCollections extends CustomGridPageBase {

    public businessUnit: BusinessUnitModel;

    @observable
    searchFilter: string = null;

    @computedFrom("dataGridVM", "dataGridVM.toggleAll", "dataGridVM._specificIds", "dataGridVM._specificIds.length")
    get anySelected(): boolean {
        return this.dataGridVM && ((this.dataGridVM.getSpecificIds() && this.dataGridVM.getSpecificIds().length > 0) || this.dataGridVM.toggleAll)
    }

    constructor(protected i18n: I18N, protected bindingEngine: BindingEngine, private dialogClientService: DialogClientService, 
        private userClaimsService: UserClaimsService, private rulesService: RulesClientService, 
        private notificationClientService: NotificationClientService) {
        super(i18n, "Rule Collections", bindingEngine)
    }

    activate(args: BusinessUnitModel) {
        this.businessUnit = args;
        this.reload();
    }

    @computedFrom(Reflection.getPropertyPath<BusinessUnitRuleCollections>(z => z.dataGridVM),
    Reflection.getPropertyPath<BusinessUnitRuleCollections>(z => z.dataGridVM.itemsCollection),
    Reflection.getPropertyPath<BusinessUnitRuleCollections>(z => z.dataGridVM.itemsCollection.length),
    "dataGridVM._specificIds", "dataGridVM._specificIds.length")
    get shouldDisableRemove(): boolean {
        return !this.dataGridVM || !this.dataGridVM.getSelectedRowsCollection() || this.dataGridVM.getSelectedRowsCollection().length <= 0;
    }

    getColumns(): DataGridColumn[] {
        return [
            {
                dataType: DataType.CustomTemplate,
                value: `<check-box checked.bind="getRowItem().isSelected"
                            changed.delegate="getDataGrid().selectRowInMultipleSelection(getRowItem())">
                        </check-box>`,
                name: "select",
                title: MobileHelper.isMobileDevice() ? "" : `<check-box class="check-box-select-all" checked.two-way="toggleAll"></check-box>`,
                parameter: {
                    item: null
                },
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: -2,
                width: MobileHelper.isMobileDevice() ? "" : "38px",
                isHidden: false,
                isSortable: false,
                disableSorting: true,
                getCellClass: () => {
                    return "selector-checkbox";
                }
            }, {
                dataType: DataType.CustomTemplate,
                value: `<div class="column">
                            <span>\${item.name}</span>
                            <div class="row">
                                <span class="global-chip" if.bind="item.isGlobal">Global</span>
                            </div>
                        </div>`,
                name: "name",
                title: "Name",
                instancePerRow: true,
                sortOrder: 0,
                columnOrder: -2,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null
                },
                width: "100px"
            },
            {
                dataType: DataType.CustomTemplate,
                value: (item, row, column, dataGridVM) => {
                    if (item && !item.isGlobal && dataGridVM) {
                        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddRulesCollectionDialog, {
                            dataGrid: dataGridVM,
                            businessUnitId: this.businessUnit.businessUnitId,
                            ruleCollectionId: item.id
                        }, true, false, false, 'right');
                    }

                    return '<div class="row">'
                        + '<div class="column"><span if.bind="item.isGlobal || !hasAddRuleCollectionPermission" >${item.displayName}</span><a if.bind="!item.isGlobal && hasAddRuleCollectionPermission" click.trigger="getDataGrid().handleClickOnButton($event,getRowItem(),getRowItem().columnKeys[`displayName`].column)" class="link" href="#">${item.displayName}</a></div>'
                        + '<comp-icon ref="itemid" class="icon-doc-lines" icon="doc-lines" if.bind="item.notes"></comp-icon>'
                        + '<comp-tooltip on-element.bind="itemid" if.bind="item.notes">${item.notes}</comp-tooltip>'
                        + '</div>'
                },
                name: "displayName",
                title: "Display Name",
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: -1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null,
                    hasAddRuleCollectionPermission: this.hasAddRuleCollectionPermission
                },
                width: "150px"
            },
            {
                sortOrder: 3,
                columnOrder: 3,
                name: "type",
                dataType: DataType.Default,
                title: "Type",
                isFilterable: false,
                disableSorting: true,
                isSortable: false
            },
            {
                sortOrder: 4,
                columnOrder: 4,
                name: "ruleCount",
                dataType: DataType.Default,
                title: "Rule Count",
                isFilterable: false,
                disableSorting: true,
                isSortable: false
            },
            {
                sortOrder: 5,
                columnOrder: 5,
                name: "childRuleCollections",
                dataType: DataType.Default,
                title: "Child Rule Collections",
                isFilterable: false,
                disableSorting: true,
                isSortable: false
            },
            {
                sortOrder: 6,
                columnOrder: 6,
                name: "isInternal",
                dataType: DataType.Boolean,
                title: "Is internal",
                isFilterable: false,
                disableSorting: true,
                isSortable: false
            },
            {
                dataType: DataType.Default,
                name: "createdBy",
                title: "Created By",
                sortOrder: 7,
                columnOrder: 7,
                isHidden: false,
                isSortable: true,
                width: "150px"
            },
            {
                dataType: DataType.Default,
                name: "lastModifiedBy",
                title: "Last Modified by",
                sortOrder: 8,
                columnOrder: 8,
                isHidden: false,
                isSortable: true,
                width: "150px"
            },
            {
                sortOrder: 9,
                columnOrder: 9,
                name: "isActive",
                dataType: DataType.Boolean,
                title: "Is Active",
                isFilterable: false,
                disableSorting: true,
                isSortable: false
            },
            {
                columnOrder: 99,
                sortOrder: 99,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "menu",
                value: `<div class="actions row justify-content-end align-items-middle">
                            <list-menu hide-menu-border="true" menu-items.bind="menuItems(item)" show.bind="isVisible">
                                <template replace-part="menu-item">
                                    <div class="row nowrap align-items-middle" click.delegate="menuItem.click($event, item)">
                                        <span class="text">\${menuItem.item.text}</span>
                                    </div>  
                                </template>
                            </list-menu>
                        </div>`,
                parameter: {
                    item: null,
                    isVisible: true,//this.canManageRoles || this.canUpdatePermissions,
                    menuItems: (item) => {
                        const menuItems: MenuItem[] = [];
                        menuItems.push({
                            isVisible: true, click: () => this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RuleCollectionAuditDialog,
                                { ruleCollectionId: item.id }, true, false, false, 'right'), item: { text: "Audit" }, classList: ''
                        });
                        menuItems.push({
                            isVisible: true, click: () => this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ViewRulesDialog,
                                { ruleCollectionId: item.id }, true, false, false, 'right'), item: { text: "View rules" }, classList: ''
                        });
                        menuItems.push({
                            isVisible: true, 
                            click: () => { 
                                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RuleCollectionAiPromptDialog, 
                                    { ruleCollectionId: item.id, businessUnitId: this.businessUnit? this.businessUnit.businessUnitId: null }, true, true, false, 'right');
                            }, 
                            item: { text: "AI Prompt" }, 
                            classList: ''
                        });
                        // menuItems.push({isVisible: true, click: () => this.deleteRuleCollection(item), item: { text: "Delete" }, classList: ''});
                        return menuItems;
                    }
                }
            }
        ];
    }

    async deleteRuleCollection(item: any[]) {
        const model = new GenericConfirmationDialogModel();
        model.confirm = this.i18N.tr('delete-text');
        model.cancel = this.i18N.tr('cancel-text');
        model.messages.push(this.i18N.tr('are-you-sure-you-want-to-delete'));
        model.title = this.i18N.tr('confirm-delete-text');
        model.onConfirm = () => this.removeRuleCollections(item);
        this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, true, false, false, 'right');
    }

    async removeRuleCollections(item: any[]) {
        const global = item.filter(x => x.isGlobal);
        if (global.length > 0) {
            const request = {
                businessUnitId: this.businessUnit.businessUnitId,
                unassignedRuleCollectionIds: global.map(x => x.ruleCollectionId)
            }
            await this.rulesService.assignBuRuleCollections(request);
        }
        
        const custom = item.filter(x => !x.isGlobal);
        if(custom.length > 0) {
            await this.rulesService.deleteRuleCollections(custom.map(x => x.ruleCollectionId));
        }

        if (this.dataGridVM)
            this.dataGridVM.reloadData();
    }

    getInitialSorting() {
        const columns: DataGridColumn[] = [];
        return columns;
    }

    reload() {
        if (!this.businessUnit) return;

        const filters = [];
        const filter = QueryFilter.getQuery("businessUnitId", this.businessUnit.businessUnitId, "eq", DataType.Number, true);
        filters.push(filter);

        if (this.searchFilter) {
            const guid = Guid.generateGuid();
            filters.push(QueryFilter.getQuery("name", this.searchFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
            filters.push(QueryFilter.getQuery("displayName", this.searchFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
        }

        this.itemSource = {
            columns: this.getColumns(),
            source: "/odata/BusinessUnitRuleCollections",
            sourceType: SourceType.ODataUrl,
            initialFilters: filters,
            initialSorting: this.getInitialSorting()
        }
    }

    @computedFrom('itemSource')
    public get hasAddRuleCollectionPermission(): boolean {
        if (!this.userClaimsService) return false;
        return this.userClaimsService.hasPermission(Claims.CanAddRuleCollectionsAtBusinessUnit);
    }

    @computedFrom('itemSource')
    public get hasRemoveRuleCollectionPermission(): boolean {
        if (!this.userClaimsService) return false;
        return this.userClaimsService.hasPermission(Claims.CanRemoveRuleCollectionsAtBusinessUnit);
    }

    addRuleCollection() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.BusinessUnitAddRuleCollectionDialog, {
            dataGrid: this.dataGridVM,
            businessUnitId: this.businessUnit.businessUnitId,
            ruleCollectionId: null
        }, true, true, false, 'right')
    }

    showDeleteDialog() {
        if (this.shouldDisableRemove)
            return;

        if ((!this.dataGridVM.toggleAll && this.dataGridVM.getSpecificIds().length == 0)) {
            this.notificationClientService.show(this.i18N.tr('no-items-selected-text'), "", NotificationType.Warning);
            return;
        }
        this.deleteRuleCollection(this.dataGridVM.getSelectedRowsCollection().map(x => x.item));
    }

    clearSearchFilter() {
        this.searchFilter = null;
    }

    searchFilterChanged() {
        this.reload()
    }

    public searchKeyDown($event): void {
        if ($event && $event.detail) {
            const code = $event.detail.keyCode;
            if (code === 13) {
                this.reload()
            }
        }
    }

    //#region Filters


    public initialFilters: QueryFilter[];
    public isMobileFilterOpen: boolean;
    public showColumnSorting: boolean = false;
    public selectedColumnSorting: number[] = [];
    public selectedFilter: { title: string, clear: () => void, apply: () => void };
    
    @computedFrom(Reflection.getPropertyPath<BusinessUnitRuleCollections>(z => z.showColumnSorting))
    public get isSubFilterOpen(): boolean {
        return this.showColumnSorting;
    }

    @computedFrom(Reflection.getPropertyPath<BusinessUnitRuleCollections>(z => z.showColumnSorting))
    public get mobileFilterSelected() {
        return this.showColumnSorting;
    }

    public toggleColumnSortingFilters() {
        if (!this.showColumnSorting) {
            this.closeAllFilters();
        }
        this.showColumnSorting = !this.showColumnSorting;
        this.selectedFilter = {
            title: "Column sorting",
            clear: () => {
            },
            apply: () => {
                this.applyColumnSortingFilter(this.selectedColumnSorting.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public applyColumnSortingFilter(items: any[], reload: boolean = true) {
        this.selectedColumnSorting = items.map(x => x.id);
        if (reload == true) this.reload();
        this.toggleColumnSortingFilters();
    }

    public closeAllFilters() {
        this.showColumnSorting = false;
        return false;
    }

    clearAllFilters(reload: boolean = true) {

        if (reload)
            this.reload();
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        this.showColumnSorting = false;
    }

    sortByMobile(column: DataGridColumn) {
        this.dataGridVM.sortingTriggered(column);
    }

    public mobileFilterApply(toggleDialog: boolean = true) {
        if (toggleDialog)
            this.toggleMobileFilterDialog();

        this.reload();
    }


    //#endregion
}
